import { type CollectionSummaryJSONObject, IkeToken, type TagJSONObject } from '@ikegps/core-api';
import { getShowRedDot } from '../../components/red.dot/is.missing.true.size';
import { CollectionTitle } from '../../components/collection/collection.title';
import { addStyles } from '../../components/theme';
import { DRAGON_ID, DRAGON_TYPE, DragonDomTypes } from '../../dom';
import { T, Translate } from '../../i18n/index';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import type { TagState } from '../../page/tag.state';
import { Services } from '../../services';
import { Highlighter } from '../../services/util/highlighter';
import { CurrentState } from '../../state/page.state';
import { TagSortState } from '../../state/tag.sort.state';
import { TagCardCheckbox } from '../tag.card.checkbox/tag.card.checkbox';
import { CSS, Style } from './collection.card.style';

addStyles(Style);

export const CollectionCard = {
	view(
		state: TagState,
		tag: TagJSONObject,
		collectionSummary: CollectionSummaryJSONObject,
		isContainerHeader = false
	): MithrilVNode {
		const hasLocation = collectionSummary.location != null;

		const attributes = CollectionCard.getAttributes(state, collectionSummary, hasLocation, isContainerHeader);
		const titleText = collectionSummary.title || collectionSummary.subtitle;
		const title = hasLocation ? titleText : `${titleText} (${Translate(T.NO_LOCATION)})`;
		const isSubtitle = !collectionSummary.title && !!collectionSummary.subtitle;

		const selectedState = {
			isSelected: () => CurrentState.Tag.isCollectionSelected(tag.objectId, collectionSummary.uuid),
			setSelected: (selected: boolean) =>
				CurrentState.Tag.selectCollection(tag.objectId, collectionSummary.uuid, selected),
		};

		const showRedDot = getShowRedDot(collectionSummary);

		const children = [
			TagCardCheckbox.view(
				state,
				selectedState,
				tag,
				null,
				Services.Token.has(IkeToken.FEATURE_COLOR_FLAG) ? collectionSummary.mapMarkerColor : null,
				showRedDot
			),
			m(
				'span',
				{
					className: isSubtitle ? `${CSS.link} ${CSS.isSubtitle}` : CSS.link,
					title,
				},
				CollectionCard.viewTitle(collectionSummary)
			),
		];

		let sortValue = TagSortState.getCollectionDisplayValue(collectionSummary);
		if (sortValue != null) {
			if (sortValue instanceof Date) {
				sortValue = Services.DateTime.formatDateTime(sortValue);
			}
			children.push(m('div', { className: CSS.info, title: Translate(TagSortState.option.title) }, sortValue));
		}

		return m('div', attributes, children);
	},

	getAttributes(
		state: TagState,
		collectionSummary: CollectionSummaryJSONObject,
		hasLocation: boolean,
		isContainerHeader = false
	) {
		const isHovered = CurrentState.hoverId === collectionSummary.revision ? CSS.isHovered : '';
		// A variant container header shares its revision with the indented "Existing" row, so the
		// "currently viewing" highlight is shown on that row, not on the header.
		const isInspected =
			!isContainerHeader && CurrentState.inspectedCollectionRevision === collectionSummary.revision
				? CSS.isInspected
				: '';
		const isNotLocated = hasLocation ? '' : CSS.noLocation;
		return {
			key: 'cc-' + collectionSummary.revision,
			className: `${CSS.container} ${isHovered} ${isInspected} ${isNotLocated}`,
			[DRAGON_ID]: collectionSummary.revision,
			[DRAGON_TYPE]: DragonDomTypes.COLLECTION,
			onclick: () => state.map.zoomToCollection(collectionSummary.revision),
		};
	},

	viewTitle(collectionSummary: CollectionSummaryJSONObject): string | MithrilVNode[] {
		const title = collectionSummary.title || collectionSummary.subtitle;
		if (title == null) {
			return CollectionTitle.view(collectionSummary.title);
		}

		if (Services.Search.filterState.hasFilter()) {
			return Highlighter.highlight(title, Services.Search.filterState.filter).parts.map((part) =>
				m('span', { className: part.highlighted ? CSS.filterMatch : null }, part.content)
			);
		}

		return title;
	},
};
